import * as React from 'react';
import './DataCaptureSection.less';
import classNames from 'classnames';
import styled from 'styled-components';
import { evalBoolean } from '../../../core/ast';
import DataCaptureComponent from './DataCaptureComponent';
import AddressHistoryEditor from './AddressHistoryEditor';
import Fieldset from '../../../commonComponents/fields/Fieldset';
import { addressHistory } from '../../../core/addressComponents';
import DataCaptureSectionProps from '../../../models/dynamicForms/DataCaptureSectionProps';
import { Component } from '../../../models/be/db/FormSpecification';

const DataCaptureSection = <T,>(props: DataCaptureSectionProps<T>) => {
	const { section } = props;
	const visibleComponents = section.components.filter(
		(c) => evalBoolean(c.visible, props.values) && c.props.type !== 'signature'
	);

	const renderDataCaptureComponent = (c: Component) => (
		<DataCaptureComponent
			key={c.id ?? c.fieldNames?.[0] ?? c.name}
			component={c}
			candidateId={props.candidateId}
			workflowId={props.workflowId}
			visibleWorkflowFields={props.visibleWorkflowFields}
			navigateToPage={props.navigateToPage}
			stripe={props.stripe}
			setSubmitButtonsDisabled={props.setSubmitButtonsDisabled}
			setBackButtonsDisabled={props.setBackButtonsDisabled}
			workflowPrimaryName={props.workflowPrimaryName}
			customerUserName={props.customerUserName}
			fileIsDownloadable={props.filesAreDownloadable}
			onFileUpload={props.onFileUpload}
			customerName={props.customerName ?? ''}
			formSpecificationPage={props.formSpecificationPage}
			customerCountryCode={props.customerCountryCode}
		/>
	);

	const renderAddressHistoryEditor = (c: Component, isFirst: boolean) => (
		<AddressHistoryWrapper key={c.id ?? 'addressHistoryEditor'} isFirst={isFirst}>
			<AddressHistoryEditor
				fieldNames={addressHistory.fieldNames}
				components={addressHistory.components}
				countryRestriction={c.props.countryRestriction}
				numberOfYearsToCover={Number(c.props.coverageYears)}
				setSubmitButtonsDisabled={props.setSubmitButtonsDisabled}
				setBackButtonsDisabled={props.setBackButtonsDisabled}
				showTwoColumnFields
			/>
		</AddressHistoryWrapper>
	);

	// The Address History editor renders as its own bordered box rather than inline alongside simple fields, so
	// the other fields are grouped into "sectionContainer" DIVs (used as a container for Occupation and Names
	// pages too) that are split around it wherever it falls, keeping every field in its configured order. Each
	// segment after the first gets its own top margin so the separate boxes don't sit flush against each other -
	// the shared .sectionContainer styling only accounts for spacing between whole Sections, not between
	// multiple segments rendered within a single Section here.
	const segments: JSX.Element[] = [];
	let currentGroup: Component[] = [];

	const flushGroup = () => {
		if (currentGroup.length > 0) {
			segments.push(
				<SectionContainerBox
					className="sectionContainer"
					isFirst={segments.length === 0}
					key={`sectionContainer-${segments.length}`}
				>
					{currentGroup.map(renderDataCaptureComponent)}
				</SectionContainerBox>
			);
			currentGroup = [];
		}
	};

	visibleComponents.forEach((c) => {
		if (c.props.type === 'addressEditor') {
			flushGroup();
			segments.push(renderAddressHistoryEditor(c, segments.length === 0));
		} else {
			currentGroup.push(c);
		}
	});
	flushGroup();

	return (
		<>
			<FieldsetWrapper title={section.heading} className={classNames('dataCaptureSection', section.className)}>
				{segments}
			</FieldsetWrapper>
		</>
	);
};

DataCaptureSection.defaultProps = {
	filesAreDownloadable: false
};

export default DataCaptureSection;

const AddressHistoryWrapper = styled.div<{ isFirst: boolean }>`
	margin-top: ${(props) => (props.isFirst ? '0' : '24px')};
	margin-bottom: 30px;
`;

const SectionContainerBox = styled.div<{ isFirst: boolean }>`
	margin-top: ${(props) => (props.isFirst ? '0' : '24px')};
`;

const FieldsetWrapper = styled(Fieldset)`
	legend {
		background: transparent !important;
	}
`;
